/**
 * `/agent` — an auto-playing, ROLEPLAYED demo of the Clickweave agent.
 *
 * Not a real LLM: it types scripted questions on the viewer's behalf and
 * reveals scripted answers, so the marketing hero can show "what the flow
 * looks like". Two panes run the SAME natural-language question in sync:
 *
 *   - LEFT  (secondary): a faithful `clickweave ask` REPL — mirrors
 *     apps/cli/src/ask/repl.tsx (logo banner, `›` prompt, `↪`/`⎿` tool
 *     rows, `⚡` agent marker, footer with model · host · status).
 *   - RIGHT (primary, elevated, overlapping): the real Ask-AI web chat —
 *     mirrors components/shell/ai-pane/ChatView.tsx (`›` user gutter,
 *     tool rows, `Sparkle` + markdown answer).
 *
 * Both panes are painted from the app's own palette tokens (the same
 * `--color-paper*` / `--color-ink` / `--color-accent-brand` the dashboard
 * uses) so switching between the Dashboard and AI Agent hero tabs feels
 * joined-up rather than jumping to a foreign terminal theme. Both call the
 * same tools against the seeded Lumina data. Chrome-less + demo-only; sized
 * to bleed off the bottom of the hero frame.
 */
import { DEMO } from '@/lib/demo';
import { Sparkle } from '@phosphor-icons/react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { type ReactElement, type ReactNode, useEffect, useRef, useState } from 'react';

export const Route = createFileRoute('/agent')({
  beforeLoad: () => {
    if (!DEMO) throw redirect({ to: '/' });
  },
  component: DemoAgent,
});

// Real CLI splash (apps/cli/src/ask/theme.ts → asciiLogoLong).
const ASCII_LOGO = `                          --.
                        .-::::-
                       :::::+::
          ..-.       .:::+:::-.
        .:+::::-   .-:+:::::-
       .:+:++:+::- -:::++::-.
     .-:+:++:::+++:-.::++++:+-
    .:+::+::+++++::::++::++-.
   -+::+:++- .::++:::++++:.
 .::++:++:.    .::=:+::+:
-:+++++::        -:+++:-
:++++++:.           ...
:++=+++-.
:=+=++-
 .-:..`;

interface FlowStep {
  q: string;
  tools: Array<{ name: string; args: string }>;
  a: string;
}

// Scripted flow — conversational (matches the real STARTERS), references
// the same Lumina fixtures the dashboard shows, and calls plausible tools.
const FLOW: FlowStep[] = [
  {
    q: 'Why did my LCP get worse?',
    tools: [
      { name: 'get_perf_trend', args: 'metric=lcp, days=180' },
      { name: 'list_deploys', args: 'days=180' },
    ],
    a: "Lumina's mobile LCP climbed to a peak of **2.8s** ~100 days ago — right after the **“New homepage hero video”** deploy on **Apr 5**. The **Apr 19** revert brought it back under 2.5s. The autoplaying hero video was delaying the LCP image.",
  },
  {
    q: 'Which page has the worst LCP?',
    tools: [{ name: 'list_pages', args: 'sort=lcp_p75, worst=true' }],
    a: '**/products/aurora-floor-lamp** — **2.92s** LCP (mobile p75), in the “needs improvement” band. It’s also your #3 page by traffic, so it’s a high-leverage fix.',
  },
  {
    q: 'How is my traffic doing?',
    tools: [{ name: 'get_traffic_summary', args: 'range=6mo' }],
    a: '**48,610** pageviews from **12,840** visitors over 6 months, **41%** bounce. **Organic search** is your biggest channel (36%) and the **US** is your top country.',
  },
];

interface ToolCall {
  name: string;
  args: string;
  done: boolean;
}
type Turn = { kind: 'user'; text: string } | { kind: 'agent'; tools: ToolCall[]; text: string };

const STARTERS = [
  'How is my site performing?',
  'Which page has the worst LCP?',
  'Show my recent audits',
];

/** Render **bold** spans in an agent answer. */
function rich(text: string): ReactNode {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? (
      // biome-ignore lint/suspicious/noArrayIndexKey: static split, stable order
      <strong key={i} className="font-semibold text-[var(--color-ink)]">
        {part.slice(2, -2)}
      </strong>
    ) : (
      // biome-ignore lint/suspicious/noArrayIndexKey: static split, stable order
      <span key={i}>{part}</span>
    ),
  );
}

function DemoAgent(): ReactElement {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');

  const chatScroll = useRef<HTMLDivElement>(null);
  const cliScroll = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pin both panes to newest content
  useEffect(() => {
    chatScroll.current?.scrollTo({ top: chatScroll.current.scrollHeight, behavior: 'smooth' });
    cliScroll.current?.scrollTo({ top: cliScroll.current.scrollHeight, behavior: 'smooth' });
  }, [turns, draft]);

  useEffect(() => {
    let cancelled = false;
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
    const type = async (text: string, speed: number) => {
      for (let i = 0; i <= text.length; i += 1) {
        if (cancelled) return;
        setDraft(text.slice(0, i));
        await sleep(speed);
      }
    };
    // Patch the most recent agent turn in place (tool progress / answer).
    const patchAgent = (fn: (a: Extract<Turn, { kind: 'agent' }>) => Turn) =>
      setTurns((t) => {
        const copy = [...t];
        for (let i = copy.length - 1; i >= 0; i -= 1) {
          if (copy[i]?.kind === 'agent') {
            copy[i] = fn(copy[i] as Extract<Turn, { kind: 'agent' }>);
            break;
          }
        }
        return copy;
      });

    async function run() {
      await sleep(1300);
      while (!cancelled) {
        for (const step of FLOW) {
          if (cancelled) return;
          await type(step.q, 32); // same question typed in both panes
          await sleep(400);
          if (cancelled) return;
          setTurns((t) => [...t, { kind: 'user', text: step.q }]);
          setDraft('');
          setTurns((t) => [...t, { kind: 'agent', tools: [], text: '' }]);
          await sleep(500);
          // Stream tool calls: running → done.
          for (const tool of step.tools) {
            if (cancelled) return;
            patchAgent((a) => ({ ...a, tools: [...a.tools, { ...tool, done: false }] }));
            await sleep(750);
            if (cancelled) return;
            patchAgent((a) => ({
              ...a,
              tools: a.tools.map((x, i) => (i === a.tools.length - 1 ? { ...x, done: true } : x)),
            }));
            await sleep(220);
          }
          await sleep(400);
          if (cancelled) return;
          patchAgent((a) => ({ ...a, text: step.a }));
          await sleep(3000);
        }
        if (cancelled) return;
        await sleep(900);
        setTurns([]);
        await sleep(1300);
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="relative h-dvh w-full overflow-hidden bg-[var(--color-paper)]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_75%_at_78%_0%,color-mix(in_oklab,var(--color-accent-brand)_9%,transparent),transparent_60%)]" />

      {/* ── CLI — `clickweave ask`, left, secondary (behind) ─────── */}
      <section className="absolute top-12 bottom-0 left-[2.5%] z-10 flex w-[47%] flex-col overflow-hidden rounded-t-xl border border-[var(--color-line)] border-b-0 bg-[var(--color-paper)] shadow-xl">
        <header className="flex items-center gap-2 border-[var(--color-line)] border-b px-4 py-3">
          <span className="flex gap-1.5">
            <span className="size-2.5 rounded-full bg-[#ff5f57]" />
            <span className="size-2.5 rounded-full bg-[#febc2e]" />
            <span className="size-2.5 rounded-full bg-[#28c840]" />
          </span>
          <span className="ml-1 text-[11px] text-[var(--color-mute)]">lumina — clickweave ask</span>
        </header>
        <div
          ref={cliScroll}
          className="min-h-0 flex-1 overflow-y-auto px-4 py-3 font-mono text-[12px] leading-relaxed"
        >
          <pre
            className="bg-gradient-to-b from-[#4f83ff] to-[var(--color-accent-brand)] bg-clip-text text-transparent"
            style={{ fontSize: '6.5px', lineHeight: '6.5px' }}
          >
            {ASCII_LOGO}
          </pre>
          <p className="mt-1.5 text-[var(--color-mute)]">
            <span className="text-[var(--color-accent-brand)]">⚡</span> clickweave · ask ·
            natural-language CLI for Clickweave
          </p>
          <p className="text-[var(--color-mute)]">Type a question, or /help for commands.</p>

          {turns.map((t, i) =>
            t.kind === 'user' ? (
              // biome-ignore lint/suspicious/noArrayIndexKey: append-only log
              <p key={i} className="mt-2.5">
                <span className="font-bold text-[var(--color-accent-brand)]">›</span>{' '}
                <span className="text-[var(--color-ink)]">{t.text}</span>
              </p>
            ) : (
              // biome-ignore lint/suspicious/noArrayIndexKey: append-only log
              <div key={i} className="mt-2">
                {t.tools.map((tool, ti) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: append-only log
                  <p key={ti} className="text-[var(--color-mute)]">
                    <span>{ti === 0 ? '↪' : '⎿'}</span>{' '}
                    <span
                      className={
                        tool.done
                          ? 'text-[var(--color-success)]'
                          : 'text-[var(--color-accent-brand)]'
                      }
                    >
                      {tool.done ? '✓' : '⠋'}
                    </span>{' '}
                    {tool.name}
                    <span className="opacity-60">({tool.args})</span>
                  </p>
                ))}
                {t.text && (
                  <p className="mt-1.5">
                    <span className="text-[var(--color-accent-brand)]">⚡</span>{' '}
                    <span className="text-[var(--color-ink-soft)]">{stripBold(t.text)}</span>
                  </p>
                )}
              </div>
            ),
          )}

          {/* Live prompt row. */}
          <p className="mt-2.5">
            <span className="font-bold text-[var(--color-accent-brand)]">›</span>{' '}
            <span className="text-[var(--color-ink)]">{draft}</span>
            <span className="ml-px inline-block animate-pulse text-[var(--color-mute)]">▌</span>
          </p>
        </div>
        <footer className="flex items-center justify-between gap-2 border-[var(--color-line)] border-t px-4 py-2.5 text-[10px] text-[var(--color-mute)]">
          <span>/help · /new · /quit · ⏎ send · Ctrl-C cancel</span>
          <span className="hidden lg:inline">Clickweave AI · api.clickweave.co · ready</span>
        </footer>
      </section>

      {/* ── Ask-AI web chat — right, primary (front, overlaps CLI) ── */}
      <section className="absolute top-4 bottom-0 right-[2.5%] z-20 flex w-[52%] flex-col overflow-hidden rounded-t-2xl border border-[var(--color-line)] border-b-0 bg-[var(--color-paper-alt)] shadow-2xl">
        <header className="flex items-center gap-2.5 border-[var(--color-line)] border-b px-5 py-4">
          <span className="flex size-6 items-center justify-center rounded-md bg-[var(--color-accent-soft)] text-[var(--color-accent-brand)]">
            <Sparkle size={14} weight="fill" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="font-semibold text-[var(--color-ink)] text-sm leading-none">
              Clickweave AI
            </p>
            <p className="mt-0.5 text-[11px] text-[var(--color-mute)] leading-none">
              Lumina · lumina.store
            </p>
          </div>
        </header>

        <div ref={chatScroll} className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
          {turns.length === 0 && (
            <div className="space-y-2.5 py-2">
              <p className="px-1 text-[11px] text-[var(--color-mute)]">Try asking…</p>
              <ul className="space-y-2">
                {STARTERS.map((s) => (
                  <li key={s}>
                    <span className="block rounded-md border border-[var(--color-line)] bg-[var(--color-paper)] px-3.5 py-2.5 text-[12px] text-[var(--color-ink)]">
                      ▸ {s}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {turns.map((t, i) =>
            t.kind === 'user' ? (
              // biome-ignore lint/suspicious/noArrayIndexKey: append-only log
              <div key={i} className="flex gap-2">
                <span className="font-bold text-[13px] text-[var(--color-accent-brand)] leading-5">
                  ›
                </span>
                <p className="flex-1 whitespace-pre-wrap text-[13px] text-[var(--color-ink)]">
                  {t.text}
                </p>
              </div>
            ) : (
              // biome-ignore lint/suspicious/noArrayIndexKey: append-only log
              <div key={i} className="space-y-2">
                {t.tools.length > 0 && (
                  <ul className="space-y-1 pl-3">
                    {t.tools.map((tool, ti) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: append-only log
                      <li key={ti} className="font-mono text-[11px] text-[var(--color-mute)]">
                        <span className="mr-1">{ti === 0 ? '↪' : '⎿'}</span>
                        <span className="mr-1">{tool.done ? '✓' : '⠋'}</span>
                        {tool.name}
                      </li>
                    ))}
                  </ul>
                )}
                {t.text && (
                  <div className="flex gap-2.5">
                    <Sparkle
                      aria-hidden="true"
                      size={13}
                      className="mt-1 shrink-0 text-[var(--color-accent-brand)]"
                    />
                    <div className="min-w-0 flex-1 text-[13px] text-[var(--color-ink-soft)] leading-[1.7]">
                      {rich(t.text)}
                    </div>
                  </div>
                )}
              </div>
            ),
          )}
        </div>

        <div className="border-[var(--color-line-soft)] border-t bg-[var(--color-paper)] px-5 py-4">
          <div className="flex items-center gap-2.5">
            <Sparkle aria-hidden="true" size={14} className="text-[var(--color-accent-brand)]" />
            <span className="min-w-0 flex-1 truncate text-sm text-[var(--color-ink)]">
              {draft || <span className="text-[var(--color-mute)]">Ask a question…</span>}
              {draft && <span className="ml-px inline-block animate-pulse">▌</span>}
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}

/** The CLI renders plain text — drop the markdown `**` emphasis markers. */
function stripBold(text: string): string {
  return text.replace(/\*\*/g, '');
}
